import React, { useMemo, useState, useEffect } from "react";
import { Heart } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * RoundUpToggle — fleet-wide reusable opt-in donation module.
 *
 * Drops into any checkout flow across the fleet. Rounds the base price up to
 * the nearest whole dollar and donates the delta to CyberKids. Opt-in only
 * (never pre-checked). Component auto-hides if the base price is already a
 * whole dollar (nothing to round up).
 *
 * Props:
 *  - basePriceCents (number)  — the base amount being checked out, in cents.
 *  - onChange ((donationCents) => void) — fires whenever the toggle flips so
 *      the parent can forward `donation_cents` to its create-checkout call.
 *  - cause (string, optional) — override the display label if needed.
 *  - testId (string, optional) — override the data-testid.
 */
export default function RoundUpToggle({
  basePriceCents,
  onChange,
  cause = "Round up for CyberKids — where different minds build what's next",
  testId = "roundup-toggle",
}) {
  const [checked, setChecked] = useState(false);

  const donationCents = useMemo(() => {
    const base = Math.max(0, Math.floor(Number(basePriceCents) || 0));
    // Default UX: pay to the penny (no round-up).
    // When the user opts in: round up to the NEXT whole dollar.
    // If the base is already a whole dollar, bump by $1 so the ask is
    // meaningful (otherwise the toggle would be a no-op).
    const rem = base % 100;
    return rem === 0 ? 100 : 100 - rem;
  }, [basePriceCents]);

  // Reset to opt-out state whenever the base price changes
  useEffect(() => {
    setChecked(false);
  }, [basePriceCents]);

  // Report changes to parent
  useEffect(() => {
    if (onChange) onChange(checked ? donationCents : 0);
  }, [checked, donationCents, onChange]);

  // Nothing to round up — hide completely
  if (donationCents <= 0 || !basePriceCents) return null;

  const donationDollars = (donationCents / 100).toFixed(2);
  const newTotalDollars = ((Math.floor(basePriceCents) + donationCents) / 100).toFixed(2);

  return (
    <label
      htmlFor={testId}
      data-testid={testId}
      className={`block cursor-pointer surface-card p-4 mt-5 border transition-all ${
        checked
          ? "border-[#FFD700]/60 bg-[#FFD700]/5"
          : "border-white/10 hover:border-[#FFD700]/40"
      }`}
    >
      <div className="flex items-start gap-3">
        <input
          id={testId}
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          data-testid={`${testId}-checkbox`}
          className="mt-1 h-4 w-4 flex-shrink-0 accent-[#FFD700] cursor-pointer"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Heart
              size={14}
              weight={checked ? "fill" : "duotone"}
              className="text-[#FFD700] flex-shrink-0"
            />
            <span className="text-sm font-semibold text-white leading-snug">{cause}</span>
          </div>
          <div className="text-xs text-white/60 font-light">
            By default you pay exactly to the penny. Opt in to round up to the next dollar — add{" "}
            <span className="text-[#FFD700] font-mono font-semibold">${donationDollars}</span>{" "}
            for a new total of{" "}
            <span className="text-white font-mono font-semibold">${newTotalDollars}</span>. 100% of
            round-ups route to the CyberKids fund.
          </div>
          <AnimatePresence>
            {checked && (
              <motion.div
                initial={{ opacity: 0, y: -4, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -4, height: 0 }}
                className="mt-2 text-[0.65rem] font-mono uppercase tracking-widest text-[#FFD700]/80"
                data-testid={`${testId}-confirm`}
              >
                // Thank you — ${donationDollars} added to total
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </label>
  );
}
